import { authenticatedFetch } from "@lumi/data";
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { ContentEditor } from "../../content-editing/ContentEditor";
import { createSubmissionAdapter } from "../../content-editing/adapters/submissionAdapter";
import { useSubmissionMutations } from "../../content-editing/hooks/useSubmissionMutations";
import type { Submission } from "../../content-editing/hooks/useSubmissions";

/**
 * Create New Submission Page - Thin Wrapper
 *
 * This component creates a new submission draft and wraps ContentEditor
 * with submission-specific logic using the adapter pattern.
 *
 * REACT STRICT MODE HANDLING:
 * React Strict Mode intentionally double-mounts components in development to help
 * detect bugs related to missing cleanup functions. This causes:
 *   1st render: mount → useEffect runs → starts API call
 *   2nd render: unmount → cleanup runs → remount → useEffect runs again
 *
 * Without proper handling, this creates duplicate API calls and database records.
 *
 * SOLUTION: Completion Flag Pattern
 * - Module-level `creationPromise`: Shared across mount cycles, reused on remount
 * - Module-level `creationCompleted`: Tracks when async creation finishes
 * - First mount: Creates promise, starts API call, flag is false
 * - Strict Mode unmount: Cleanup checks flag (false), does nothing
 * - Strict Mode remount: Reuses existing promise, no new API call
 * - API completes: Sets flag to true, sets submissionId
 * - Real navigation away: Cleanup sees flag is true, resets both
 * - Next visit: Both null, creates fresh submission
 *
 * RESULT: Single API call per navigation, handles both Strict Mode and real navigation.
 */
let creationPromise: Promise<string | null> | null = null;
let creationCompleted = false;

/* eslint-disable max-lines-per-function */
export function CreateSubmission(): ReactElement {
  const navigate = useNavigate();
  const { accessToken, refreshAuth } = useAuth();
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get mutation functions
  const { createSubmission, updateSubmission, submitForReview, resubmit } = useSubmissionMutations();

  // Create initial submission on mount (only once per navigation intent)
  useEffect(() => {
    // Skip if already have a submission ID
    if (submissionId) return;

    // Check if we already have a promise in progress (from Strict Mode remount)
    creationPromise ??= (async () => {
      try {
        // Create a draft submission with minimal required data
        const submission = await createSubmission({
          title: "Untitled Submission",
          releaseYear: new Date().getFullYear(),
          genres: [], // Empty array instead of undefined
        });

        if (submission?.id) {
          creationCompleted = true; // Signal that creation finished successfully
          return submission.id;
        } else {
          throw new Error("Failed to create submission");
        }
      } catch (err) {
        console.error("Error creating submission:", err);
        throw err;
      }
    })();

    // Whether this is the first mount or a remount, wait for the same promise
    setLoading(true);
    creationPromise
      .then((id) => {
        if (id) {
          setSubmissionId(id);
        } else {
          setError("Failed to create submission");
        }
      })
      .catch(() => {
        setError("Failed to create submission. Please refresh and try again.");
      })
      .finally(() => {
        setLoading(false);
      });

    // Cleanup: Only reset after creation completes
    // - During Strict Mode unmount: creationCompleted is false → no reset → remount reuses promise
    // - After real navigation: creationCompleted is true → reset both → next visit creates new submission
    return () => {
      if (creationCompleted) {
        creationPromise = null;
        creationCompleted = false;
      }
    };
  }, [createSubmission, submissionId]);
  // Dependencies: createSubmission (stable via useCallback), submissionId (prevents re-creation)

  // Create refetch function
  const refetchSubmission = useCallback(async (): Promise<{ data: Submission | null }> => {
    if (!accessToken || !submissionId) {
      return { data: null };
    }

    const url = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/submissions/${submissionId}`;

    try {
      const result = await authenticatedFetch<{ submission: Submission }>(
        url,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      return { data: result.submission };
    } catch (err) {
      console.error("Error refetching submission:", err);
      return { data: null };
    }
  }, [accessToken, refreshAuth, submissionId]);

  // Create adapter that connects ContentEditor to submission logic
  const adapter = useMemo(() => {
    if (!submissionId) return null;

    return createSubmissionAdapter(
      submissionId,
      loading,
      error,
      refetchSubmission,
      updateSubmission,
      submitForReview,
      resubmit,
    );
  }, [submissionId, loading, error, refetchSubmission, updateSubmission, submitForReview, resubmit]);

  // Handle navigation back to submissions list
  const handleCancel = () => {
    navigate("/provider");
  };

  // Loading state
  if (loading) {
    return (
      <div className="mx-auto py-12 text-center">
        <p className="text-neutral-400">Creating new submission...</p>
      </div>
    );
  }

  // Error state
  if (error || !submissionId || !adapter) {
    return (
      <div className="mx-auto py-12 text-center space-y-4">
        <p className="text-red-400">{error || "Failed to create submission"}</p>
        <button
          onClick={handleCancel}
          className="inline-block px-6 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Render ContentEditor with submission adapter
  return (
    <ContentEditor
      id={submissionId}
      adapter={adapter}
      onCancel={handleCancel}
      breadcrumbPath={[
        { to: "/provider", label: "My Submissions" },
        { to: "", label: "New Submission" },
      ]}
    />
  );
}
